import { useState } from 'react';
import { exportReport } from '../../utils/api';

const FONT = "'DM Sans', system-ui, sans-serif";

const C = {
  bg:      '#FFF8F7',
  text:    '#3D1F02',
  heading: '#6B3E0A',
  accent:  '#AD795B',
  border:  '#E8D5C4',
  card:    '#ffffff',
  green:   '#2E7D5E',
  greenBg: '#F0FAF5',
  red:     '#B91C1C',
  redBg:   '#FEF2F2',
};

const today         = new Date().toISOString().slice(0, 10);
const daysAgo = (n) => new Date(Date.now() - n * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

// SEPARATE FROM adminShared's RANGE_PRESETS on purpose, and left that way.
// This picker fills two date inputs for one report download; that one owns a
// tab's live queries, has a Custom mode and a rolling 24h option, and its ids
// are read back elsewhere. Merging them would drag 'Last 24 hours', '6m' and
// 'Custom' onto a report that wants none of them.
//
// What they DO share is the fortnight, added 2026-09-05 alongside the '14d' in
// RANGE_PRESETS so the two lists offer the same spans where they overlap. The
// wording follows THIS list's neighbours ("Last 14 days"), not that one's
// ("14d") — a label reading "14d" between "Last 7 days" and "Last 30 days"
// would look like a mistake.
const PRESETS = [
  { label: 'Last 7 days',  from: daysAgo(7),  to: today },
  { label: 'Last 14 days', from: daysAgo(14), to: today },
  { label: 'Last 30 days', from: daysAgo(30), to: today },
  { label: 'Last 90 days', from: daysAgo(90), to: today },
  { label: 'All time',     from: '',          to: ''    },
];
// What the page opens on. The highlighted pill AND the date inputs both come
// from this one entry, so they can never disagree (before 2026-09-30 the
// inputs opened on 30 days while index 1 — "Last 14 days" since that pill was
// added — was highlighted).
const DEFAULT_PRESET = PRESETS.findIndex(p => p.label === 'Last 30 days');

// Revenue scope. Defaults to RY only: this is a FUNNEL report, and the
// whole-business figure is Ben's entire historical Stripe income including
// coaching unrelated to this funnel — the one that misleads if nobody touches
// the control. The chosen scope is printed inside the report itself.
const SCOPES = [
  { id: 'ry',  label: 'RY only',        hint: 'Charges tagged product=RY since launch — matches the dashboard.' },
  { id: 'all', label: 'Whole business', hint: 'Every succeeded Stripe charge, including coaching income unrelated to this funnel.' },
];

const REPORT_SECTIONS = [
  { num: '1', title: 'Funnel Overview',       desc: 'Stage-by-stage counts and conversion rates with health indicators' },
  { num: '2', title: 'Revenue',               desc: 'Period total, transaction count, and average sale value' },
  { num: '3', title: 'Course Progress',       desc: 'Day 1–7 completion counts and retention from Day 1' },
  { num: '4', title: 'Email Performance',     desc: 'Open, click, and bounce rates per sequence with benchmarks' },
  { num: '5', title: 'Traffic Sources',       desc: 'Opt-in breakdown by UTM source with masterclass watch rates' },
  { num: '6', title: 'Bottleneck Analysis',   desc: 'Weakest conversion stage ranked and identified automatically' },
  { num: '7', title: 'Goals vs Actuals',      desc: 'Current targets and actuals from your Goals & Targets tab' },
  { num: '8', title: 'Suggested Focus Areas', desc: 'Auto-generated action items based on benchmark gaps' },
  { num: '9', title: 'Video Performance',     desc: 'Masterclass and RY landing page: play rates, milestone drop-off, CTA engagement' },
];

export const AdminExport = () => {
  const [from, setFrom]               = useState(PRESETS[DEFAULT_PRESET].from);
  const [to, setTo]                   = useState(PRESETS[DEFAULT_PRESET].to);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const [lastFile, setLastFile]       = useState('');
  const [activePreset, setActivePreset] = useState(DEFAULT_PRESET); // index into PRESETS
  const [scope, setScope]             = useState('ry');

  const applyPreset = (idx) => {
    setActivePreset(idx);
    setFrom(PRESETS[idx].from);
    setTo(PRESETS[idx].to);
  };

  const handleExport = async () => {
    setError('');
    setLastFile('');
    setLoading(true);
    try {
      const res = await exportReport(from || undefined, to || undefined, scope);
      const blob = new Blob([res.data], { type: 'text/markdown' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      const safeFrom = (from || 'start').replace(/-/g, '');
      const safeTo   = (to   || 'today').replace(/-/g, '');
      // Scope is in the filename as well as inside the file, so two downloads of
      // the same period do not overwrite each other or get mixed up on disk.
      const scopeTag = scope === 'all' ? 'WholeBusiness' : 'RYonly';
      const filename = `Funnel_Report_${safeFrom}_to_${safeTo}_${scopeTag}.md`;
      a.href     = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setLastFile(filename);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Export failed — check the console for details');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ fontFamily: FONT, color: C.text }}>

      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 800, color: C.heading }}>
          Export Report
        </h2>
        <p style={{ margin: 0, fontSize: 14, color: C.accent }}>
          Generate a full funnel analytics snapshot as a Markdown file — paste into Notion, Obsidian, or any text editor.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>

        {/* Left: config card */}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: '24px 28px', flex: '0 0 420px', minWidth: 300 }}>

          {/* Presets */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.accent, letterSpacing: '0.08em', marginBottom: 8 }}>
              QUICK PRESETS
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {PRESETS.map((p, idx) => (
                <button
                  key={p.label}
                  onClick={() => applyPreset(idx)}
                  style={{
                    padding: '6px 14px',
                    borderRadius: 20,
                    border: `1.5px solid ${activePreset === idx ? C.heading : C.border}`,
                    background: activePreset === idx ? C.heading : C.bg,
                    color: activePreset === idx ? '#fff' : C.text,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: FONT,
                    transition: 'all 0.15s',
                  }}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Date pickers */}
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 24 }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: C.accent, letterSpacing: '0.08em', marginBottom: 6 }}>
                FROM
              </label>
              <input
                type="date"
                value={from}
                onChange={e => { setFrom(e.target.value); setActivePreset(-1); }}
                style={{
                  width: '100%', boxSizing: 'border-box',
                  padding: '9px 10px', fontSize: 13,
                  border: `1px solid ${C.border}`, borderRadius: 8,
                  fontFamily: FONT, color: C.text, background: C.bg, outline: 'none',
                }}
              />
            </div>
            <div style={{ fontSize: 16, color: C.border, paddingBottom: 8, flexShrink: 0 }}>→</div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: C.accent, letterSpacing: '0.08em', marginBottom: 6 }}>
                TO
              </label>
              <input
                type="date"
                value={to}
                onChange={e => { setTo(e.target.value); setActivePreset(-1); }}
                style={{
                  width: '100%', boxSizing: 'border-box',
                  padding: '9px 10px', fontSize: 13,
                  border: `1px solid ${C.border}`, borderRadius: 8,
                  fontFamily: FONT, color: C.text, background: C.bg, outline: 'none',
                }}
              />
            </div>
          </div>

          {/* Revenue scope — same pill convention as the presets above, because
              this is the same kind of choice: it changes what gets generated.
              The report is a file, so it must be chosen before download. */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.accent, letterSpacing: '0.08em', marginBottom: 8 }}>
              REVENUE SCOPE
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {SCOPES.map(s => (
                <button
                  key={s.id}
                  onClick={() => setScope(s.id)}
                  aria-pressed={scope === s.id}
                  style={{
                    padding: '6px 14px',
                    borderRadius: 20,
                    border: `1.5px solid ${scope === s.id ? C.heading : C.border}`,
                    background: scope === s.id ? C.heading : C.bg,
                    color: scope === s.id ? '#fff' : C.text,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: FONT,
                    transition: 'all 0.15s',
                  }}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <div style={{ fontSize: 11, color: C.accent, marginTop: 8, lineHeight: 1.5 }}>
              {SCOPES.find(s => s.id === scope)?.hint}
            </div>
          </div>

          {/* Download button */}
          <button
            onClick={handleExport}
            disabled={loading}
            style={{
              width: '100%',
              padding: '13px 24px',
              background: loading ? C.border : C.heading,
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              fontSize: 15,
              fontWeight: 700,
              cursor: loading ? 'not-allowed' : 'pointer',
              fontFamily: FONT,
              letterSpacing: '0.01em',
              transition: 'background 0.15s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            {loading ? (
              <>
                <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</span>
                Generating report…
              </>
            ) : (
              '↓  Download Report'
            )}
          </button>

          {/* Error state */}
          {error && (
            <div style={{
              marginTop: 12,
              padding: '10px 14px',
              background: C.redBg,
              border: `1px solid #FECACA`,
              borderRadius: 8,
              fontSize: 13,
              color: C.red,
              fontFamily: FONT,
            }}>
              ✕ {error}
            </div>
          )}

          {/* Success state */}
          {lastFile && !error && !loading && (
            <div style={{
              marginTop: 12,
              padding: '10px 14px',
              background: C.greenBg,
              border: `1px solid ${C.green}44`,
              borderRadius: 8,
              fontSize: 13,
              color: C.green,
              fontFamily: FONT,
            }}>
              ✓ Downloaded: <strong>{lastFile}</strong>
            </div>
          )}
        </div>

        {/* Right: what's inside */}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: '24px 28px', flex: 1, minWidth: 260 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.heading, marginBottom: 16 }}>
            What's in the report
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {REPORT_SECTIONS.map(({ num, title, desc }) => (
              <div key={num} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div style={{
                  flexShrink: 0,
                  width: 26, height: 26,
                  borderRadius: 6,
                  background: C.bg,
                  border: `1px solid ${C.border}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 800, color: C.heading,
                  fontFamily: FONT,
                }}>
                  {num}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text, lineHeight: 1.3 }}>{title}</div>
                  <div style={{ fontSize: 12, color: C.accent, marginTop: 2, lineHeight: 1.5 }}>{desc}</div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 12, color: C.accent, lineHeight: 1.7 }}>
              <strong style={{ color: C.text }}>Health indicators</strong> — 🟢 healthy  🟡 watch  🔴 concerning, based on your configured benchmarks.
              <br />
              <strong style={{ color: C.text }}>Leave dates blank</strong> for all-time data.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
